/**
 * The demo's Meta campaign report: one campaign tree, summed over whatever range
 * and level the page asks for. Kept apart from `features/integrations/mock.ts`
 * (the connection itself) but reads its `MockMetaStore.state()` for `connected` /
 * `mappedAccounts` / `providerLag`, so the two pages can never disagree about
 * whether Meta is set up.
 *
 * One campaign is active with real spend; the other is fully paused with none —
 * enough to show the provider-attributed badges, a drill-down with a genuinely
 * empty ad set, and a currency the reader does not have to invent.
 */

import { wireSortField } from '@/api/advertising'
import {
  ADVERTISING_METRIC_KEYS,
  TMT_ATTRIBUTED_METRIC_KEYS,
  type AdvertisingFreshness,
  type AdvertisingLevel,
  type AdvertisingMetrics,
  type AdvertisingQuery,
  type AdvertisingReport,
  type AdvertisingRow,
  type AttributionModel,
  type MetaIntegrationState,
  type TmtAttributedMetrics,
  type TmtUnmatchedPaid,
} from '@/api/types'
import { addDays, dayCount } from '@/lib/filters'

import { TMT_METRIC_KEY, type TmtColumnKey } from './metrics'

interface DemoAd {
  id: string
  name: string
  status: string
  effectiveStatus: string
  adSetId: string
  spendPerDayMinor: number
  impressionsPerDay: number
  clicksPerDay: number
  purchasesPerDay: number
  revenuePerPurchaseMinor: number
}

interface DemoAdSet {
  id: string
  name: string
  status: string
  effectiveStatus: string
  campaignId: string
}

interface DemoCampaign {
  id: string
  name: string
  status: string
  effectiveStatus: string
}

const CAMPAIGNS: readonly DemoCampaign[] = [
  { id: 'adcmp_demo_summer', name: 'Summer sell-out', status: 'ACTIVE', effectiveStatus: 'ACTIVE' },
  { id: 'adcmp_demo_autumn', name: 'Autumn draw', status: 'PAUSED', effectiveStatus: 'PAUSED' },
]

const AD_SETS: readonly DemoAdSet[] = [
  { id: 'adset_demo_uk', name: 'UK 25-54', status: 'ACTIVE', effectiveStatus: 'ACTIVE', campaignId: 'adcmp_demo_summer' },
  {
    id: 'adset_demo_lookalike',
    name: 'Lookalike 1%',
    status: 'PAUSED',
    effectiveStatus: 'PAUSED',
    campaignId: 'adcmp_demo_summer',
  },
  {
    id: 'adset_demo_retarget',
    name: 'Retargeting',
    status: 'PAUSED',
    effectiveStatus: 'CAMPAIGN_PAUSED',
    campaignId: 'adcmp_demo_autumn',
  },
]

const ADS: readonly DemoAd[] = [
  {
    id: 'ad_demo_hero',
    name: 'Hero video',
    status: 'ACTIVE',
    effectiveStatus: 'ACTIVE',
    adSetId: 'adset_demo_uk',
    spendPerDayMinor: 4200,
    impressionsPerDay: 9000,
    clicksPerDay: 180,
    purchasesPerDay: 6,
    revenuePerPurchaseMinor: 3500,
  },
  {
    id: 'ad_demo_carousel',
    name: 'Product carousel',
    status: 'ACTIVE',
    effectiveStatus: 'ACTIVE',
    adSetId: 'adset_demo_uk',
    spendPerDayMinor: 2100,
    impressionsPerDay: 6000,
    clicksPerDay: 90,
    purchasesPerDay: 2,
    revenuePerPurchaseMinor: 3500,
  },
  {
    id: 'ad_demo_static',
    name: 'Static banner',
    status: 'PAUSED',
    effectiveStatus: 'PAUSED',
    adSetId: 'adset_demo_lookalike',
    spendPerDayMinor: 0,
    impressionsPerDay: 0,
    clicksPerDay: 0,
    purchasesPerDay: 0,
    revenuePerPurchaseMinor: 0,
  },
  {
    id: 'ad_demo_retarget1',
    name: 'Retarget creative',
    status: 'PAUSED',
    effectiveStatus: 'CAMPAIGN_PAUSED',
    adSetId: 'adset_demo_retarget',
    spendPerDayMinor: 0,
    impressionsPerDay: 0,
    clicksPerDay: 0,
    purchasesPerDay: 0,
    revenuePerPurchaseMinor: 0,
  },
]

/** The previous period runs 12% lighter, so a comparison has a believable delta. */
const PREVIOUS_FACTOR = 0.88

const DEFAULT_MODEL: AttributionModel = 'paid_click'
/** TmT's own attributed purchases are always a share of Meta's *reported* ones —
 * the two never agree exactly, which is the whole point of showing both — and
 * the share shifts a little with the model, so switching the selector visibly
 * moves the TmT columns without a second demo dataset to hand-tune. */
const MODEL_FACTOR: Record<AttributionModel, number> = {
  first_touch: 0.62,
  last_touch: 0.71,
  last_non_direct: 0.65,
  paid_click: 0.68,
}
const NEW_CUSTOMER_SHARE = 0.4
const LOOKBACK_DAYS = 30

/** TmT's own figures for one period's summed provider metrics, under `model` — the
 * ratios follow the same "period sums, not a mean of daily ratios" rule the real
 * join uses. */
function tmtFor(metrics: AdvertisingMetrics, model: AttributionModel): TmtAttributedMetrics {
  const factor = MODEL_FACTOR[model]
  const purchases = Math.round(metrics.providerPurchases * factor)
  const revenueMinor = Math.round(metrics.providerPurchaseRevenueMinor * factor)
  const newCustomers = Math.round(purchases * NEW_CUSTOMER_SHARE)
  return {
    model,
    tmtAttributed: true,
    purchases,
    revenueMinor,
    newCustomers,
    cpaMinor: purchases ? Math.round(metrics.spendMinor / purchases) : null,
    roas: metrics.spendMinor ? revenueMinor / metrics.spendMinor : null,
    costPerNewCustomerMinor: newCustomers ? Math.round(metrics.spendMinor / newCustomers) : null,
  }
}

function metricsFor(spendMinor: number, impressions: number, clicks: number, purchases: number, revenueMinor: number): AdvertisingMetrics {
  return {
    spendMinor,
    impressions,
    clicks,
    landingPageViews: Math.round(clicks * 0.7),
    ctr: impressions ? clicks / impressions : null,
    cpcMinor: clicks ? Math.round(spendMinor / clicks) : null,
    cpmMinor: impressions ? Math.round((spendMinor * 1000) / impressions) : null,
    providerPurchases: purchases,
    providerPurchaseRevenueMinor: revenueMinor,
    providerCpaMinor: purchases ? Math.round(spendMinor / purchases) : null,
    providerRoas: spendMinor ? revenueMinor / spendMinor : null,
  }
}

function adTotals(ad: DemoAd, days: number, factor: number): AdvertisingMetrics {
  const spend = Math.round(ad.spendPerDayMinor * days * factor)
  const impressions = Math.round(ad.impressionsPerDay * days * factor)
  const clicks = Math.round(ad.clicksPerDay * days * factor)
  const purchases = Math.round(ad.purchasesPerDay * days * factor)
  return metricsFor(spend, impressions, clicks, purchases, purchases * ad.revenuePerPurchaseMinor)
}

function sum(metrics: readonly AdvertisingMetrics[]): AdvertisingMetrics {
  const spend = metrics.reduce((total, m) => total + m.spendMinor, 0)
  const impressions = metrics.reduce((total, m) => total + m.impressions, 0)
  const clicks = metrics.reduce((total, m) => total + m.clicks, 0)
  const purchases = metrics.reduce((total, m) => total + m.providerPurchases, 0)
  const revenue = metrics.reduce((total, m) => total + m.providerPurchaseRevenueMinor, 0)
  return metricsFor(spend, impressions, clicks, purchases, revenue)
}

interface DemoObject {
  id: string
  name: string
  status: string
  effectiveStatus: string
}

function objectsAt(level: AdvertisingLevel, parentId: string | undefined): DemoObject[] {
  if (level === 'campaign') return [...CAMPAIGNS]
  if (level === 'ad_set') return AD_SETS.filter((set) => set.campaignId === parentId)
  return ADS.filter((ad) => ad.adSetId === parentId)
}

function adsFor(level: AdvertisingLevel, objectId: string): DemoAd[] {
  if (level === 'ad') return ADS.filter((ad) => ad.id === objectId)
  if (level === 'ad_set') return ADS.filter((ad) => ad.adSetId === objectId)
  const setIds = new Set(AD_SETS.filter((set) => set.campaignId === objectId).map((set) => set.id))
  return ADS.filter((ad) => setIds.has(ad.adSetId))
}

function sortValue(row: AdvertisingRow, field: string): string | number | null {
  if (field === 'name') return (row.name ?? '').toLowerCase()
  if (field in TMT_METRIC_KEY) return row.tmt[TMT_METRIC_KEY[field as TmtColumnKey]]
  return row.metrics[field as keyof AdvertisingMetrics]
}

/**
 * ``rows``, sorted by ``field`` — a null ratio (no impressions, no clicks, no
 * spend) always sorts last, in *either* direction. Mirrors the API's own
 * ``query._sorted_rows``: reversing a comparator that encodes "is null" as
 * part of its ordering would put nulls first on a descending sort instead of
 * last, which is the exact bug that rule exists to rule out.
 */
function sortRows(rows: AdvertisingRow[], sort: string | undefined): AdvertisingRow[] {
  const field = sort?.startsWith('-') ? sort.slice(1) : (sort ?? 'spendMinor')
  const descending = sort ? sort.startsWith('-') : true
  const withValue = rows.filter((row) => sortValue(row, field) !== null)
  const withoutValue = rows.filter((row) => sortValue(row, field) === null)
  withValue.sort((a, b) => {
    const left = sortValue(a, field) as string | number
    const right = sortValue(b, field) as string | number
    return left < right ? -1 : left > right ? 1 : 0
  })
  if (descending) withValue.reverse()
  withoutValue.sort((a, b) => a.id.localeCompare(b.id))
  return [...withValue, ...withoutValue]
}

export class MockAdvertisingStore {
  constructor(
    private readonly connection: () => MetaIntegrationState,
    private readonly currency: () => string,
  ) {}

  report(query: AdvertisingQuery): AdvertisingReport {
    const meta = this.connection()
    const days = dayCount(query.from, query.to)
    const model = query.model ?? DEFAULT_MODEL

    const connected = meta.status !== 'not_connected'
    const mappedAccounts = meta.adAccounts.length
    const active = connected && mappedAccounts > 0

    // A disconnected or unmapped tenant has never imported anything: no rows,
    // the same as the real API answering an empty `ad_insights_daily`.
    const objects = active ? objectsAt(query.level, query.parentId) : []
    const matching = query.q ? objects.filter((object) => object.name.toLowerCase().includes(query.q!.toLowerCase())) : objects

    const rows: AdvertisingRow[] = matching.map((object) => {
      const ads = adsFor(query.level, object.id)
      const current = sum(ads.map((ad) => adTotals(ad, days, 1)))
      const previous = query.compare ? sum(ads.map((ad) => adTotals(ad, days, PREVIOUS_FACTOR))) : null
      return {
        id: object.id,
        name: object.name,
        status: object.status,
        effectiveStatus: object.effectiveStatus,
        metrics: current,
        previous,
        tmt: tmtFor(current, model),
        tmtPrevious: previous ? tmtFor(previous, model) : null,
      }
    })
    const sortedRows = sortRows(rows, query.sort)
    // Totals cover every matching row regardless of the page requested — the
    // same rule the API's `run()` follows (totals are summed before paging).
    const totals = sum(sortedRows.map((row) => row.metrics))
    const totalsPrevious = query.compare
      ? sum(sortedRows.map((row) => row.previous ?? metricsFor(0, 0, 0, 0, 0)))
      : null
    const offset = query.offset ?? 0
    const limit = query.limit ?? sortedRows.length
    const page = sortedRows.slice(offset, offset + limit)

    const previousStart = query.compare ? addDays(query.from, -days) : null
    const previousEnd = query.compare ? addDays(query.from, -1) : null

    // A demo figure of the same shape the real join reports: a handful of paid
    // clicks the (fictional) hierarchy import never resolved. Independent of
    // `level`/`parentId`, the same discard rule the real route follows.
    const unmatchedPaid: TmtUnmatchedPaid =
      active && query.level === 'campaign' && !query.parentId
        ? { purchases: 2, revenueMinor: Math.round(1500 * (1 - MODEL_FACTOR[model])) }
        : { purchases: 0, revenueMinor: 0 }

    return {
      source: 'meta',
      start: query.from,
      end: query.to,
      level: query.level,
      parentId: query.parentId ?? null,
      compared: query.compare,
      previousStart,
      previousEnd,
      currency: this.currency(),
      attributionWindows: active ? ['7d_click,1d_view'] : [],
      rows: page,
      totals,
      totalsPrevious,
      tmtModel: model,
      tmtLookbackDays: LOOKBACK_DAYS,
      tmtTotals: tmtFor(totals, model),
      tmtTotalsPrevious: totalsPrevious ? tmtFor(totalsPrevious, model) : null,
      tmtUnmatchedPaid: unmatchedPaid,
      asOf: this.freshness(meta, active),
    }
  }

  csv(query: AdvertisingQuery): Blob {
    const report = this.report(query)
    // One column list, the same names and order as `ADVERTISING_METRIC_KEYS` —
    // the real CSV's own contract — so a metric added there cannot drift from
    // what the demo downloads. The six TmT columns follow, headed the same way
    // the real export's `_tmt_csv_headers` names them.
    const columns = [
      'id',
      'name',
      'status',
      'effective_status',
      ...ADVERTISING_METRIC_KEYS.map(wireSortField),
      ...tmtCsvHeaders(report.tmtModel),
    ]
    const lines = [columns.join(',')]
    for (const row of report.rows) {
      const values = ADVERTISING_METRIC_KEYS.map((key) => row.metrics[key] ?? '')
      const tmtValues = TMT_ATTRIBUTED_METRIC_KEYS.map((key) => row.tmt[key] ?? '')
      lines.push(
        [
          csvSafe(row.id),
          csvSafe(row.name ?? ''),
          row.status ?? '',
          row.effectiveStatus ?? '',
          ...values,
          ...tmtValues,
        ].join(','),
      )
    }
    return new Blob([lines.join('\n')], { type: 'text/csv' })
  }

  private freshness(meta: MetaIntegrationState, active: boolean): AdvertisingFreshness {
    if (meta.status === 'not_connected') {
      return {
        generatedAt: new Date().toISOString(),
        lastRecentImport: null,
        providerLag: false,
        connected: false,
        mappedAccounts: 0,
      }
    }
    return {
      generatedAt: new Date().toISOString(),
      lastRecentImport: active ? meta.lastImports.insights : null,
      providerLag: meta.providerLag,
      connected: true,
      mappedAccounts: meta.adAccounts.length,
    }
  }
}

/** What each `TMT_ATTRIBUTED_METRIC_KEYS` entry reads as in a header, before the
 * model is appended — mirrors the real export's `_TMT_CSV_LABELS`. */
const TMT_CSV_LABELS: Record<(typeof TMT_ATTRIBUTED_METRIC_KEYS)[number], string> = {
  purchases: 'purchases',
  revenueMinor: 'revenue',
  newCustomers: 'new customers',
  cpaMinor: 'CPA',
  roas: 'ROAS',
  costPerNewCustomerMinor: 'cost per new customer',
}

function tmtCsvHeaders(model: AttributionModel): string[] {
  return TMT_ATTRIBUTED_METRIC_KEYS.map((key) => `TmT ${TMT_CSV_LABELS[key]} (${model})`)
}

/** A spreadsheet reads a cell starting with one of these as a formula — the same
 * neutralisation the real export's `_csv_safe` applies. Exported for its own test. */
export const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r']

export function csvSafe(value: string): string {
  const neutralised = FORMULA_PREFIXES.some((prefix) => value.startsWith(prefix)) ? `'${value}` : value
  return /[,"\n]/.test(neutralised) ? `"${neutralised.replace(/"/g, '""')}"` : neutralised
}

/** One store per demo client and tenant. */
const stores = new WeakMap<object, Map<string, MockAdvertisingStore>>()

export function advertisingStoreFor(
  owner: object,
  tenantId: string,
  connection: () => MetaIntegrationState,
  currency: () => string,
): MockAdvertisingStore {
  let byTenant = stores.get(owner)
  if (!byTenant) {
    byTenant = new Map()
    stores.set(owner, byTenant)
  }
  let store = byTenant.get(tenantId)
  if (!store) {
    store = new MockAdvertisingStore(connection, currency)
    byTenant.set(tenantId, store)
  }
  return store
}
